import { useCTAVisualSource } from "@jugl-web/domain-resources/chat-bot/hooks/chatBotMediaSourceResolvers";
import { cx } from "@jugl-web/utils";
import { FC, useMemo } from "react";
import { CustomCTAVisualPreview } from "../../../CustomCTAVisualPreview";
import { DefaultCTAVisualPreview } from "../../../DefaultCTAVisualPreview";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import { ReactComponent as CrossIcon } from "./assets/cross.svg";

const CTA_BUBBLE_MIN_WIDTH_PX = 250;
const CTA_BUBBLE_MAX_WIDTH_PX = 350;
const CTA_VISUAL_HEIGHT_PX = 160;
const MAX_PREVIEW_CTAS = 2;

export interface CTAPreviewProps {
  ctas: { id: string; value: string }[];
  ctaVisual: File | string | null;
  alignment: "left" | "right";
  buttonSize: number;
}

const isDefaultCTAVisual = (visual: string | File | null): boolean =>
  typeof visual === "string" && visual.startsWith("default");

export const CTAPreview: FC<CTAPreviewProps> = ({
  ctas,
  alignment,
  buttonSize,
  ctaVisual,
}) => {
  const visibleCTAs = useMemo(
    () =>
      ctas
        .filter((cta) => cta.value.trim().length > 0)
        .slice(0, MAX_PREVIEW_CTAS),
    [ctas]
  );

  const ctaPreviewContainerClassName = cx(
    "group flex flex-col gap-2 transition-transform duration-300",
    alignment === "right"
      ? "self-end hover:translate-x-1"
      : "self-start hover:-translate-x-1"
  );

  const ctaPreviewContainerStyle = {
    minWidth: CTA_BUBBLE_MIN_WIDTH_PX,
    maxWidth: CTA_BUBBLE_MAX_WIDTH_PX,
  };

  if (visibleCTAs.length === 0) {
    if (!ctaVisual) {
      return null;
    }

    return (
      <div
        className={ctaPreviewContainerClassName}
        style={ctaPreviewContainerStyle}
      >
        <CTABubble
          text=""
          visual={ctaVisual}
          alignment={alignment}
          buttonSize={buttonSize}
          showCloseButton
          showArrow={false}
        />
      </div>
    );
  }

  const isSingle = visibleCTAs.length === 1;

  return (
    <div
      className={ctaPreviewContainerClassName}
      style={ctaPreviewContainerStyle}
    >
      {visibleCTAs.map((cta, index) => {
        const isFirst = index === 0;
        const isLast = index === visibleCTAs.length - 1;

        return (
          <CTABubble
            key={cta.id}
            text={cta.value}
            visual={isFirst ? ctaVisual : null}
            alignment={alignment}
            buttonSize={buttonSize}
            showCloseButton={isFirst}
            showArrow={isLast || isSingle}
          />
        );
      })}
    </div>
  );
};

export interface CTABubbleProps {
  text: string;
  visual: File | string | null;
  alignment: "left" | "right";
  buttonSize: number;
  showCloseButton: boolean;
  showArrow: boolean;
}

export const CTABubble: FC<CTABubbleProps> = ({
  text,
  visual,
  alignment,
  buttonSize,
  showCloseButton,
  showArrow,
}) => {
  const visualSrc = useCTAVisualSource(visual);

  const closeButton = showCloseButton ? (
    <button
      type="button"
      className={cx(
        "pointer-events-none absolute top-[-10px] right-[-10px] z-10",
        "flex h-8 w-8 items-center justify-center rounded-full border-none bg-white",
        "cursor-default shadow-[0_13px_56px_rgba(0,0,0,0.16)]",
        "opacity-0 transition duration-200",
        "group-hover:pointer-events-auto group-hover:opacity-100",
        "hover:scale-110 active:scale-90"
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <CrossIcon />
    </button>
  ) : null;

  const textBlock = (
    <span className="font-secondary block text-sm font-medium leading-[1.3] text-[#1a2023]">
      {text}
    </span>
  );

  if (visual && visualSrc) {
    return (
      <div className="relative w-full cursor-default select-none">
        <div className="overflow-hidden rounded-[28px] bg-white shadow-[0_13px_56px_0_rgba(0,0,0,0.16)]">
          <div
            className="relative w-full shrink-0 overflow-hidden bg-[#FAFAFA]"
            style={{ height: CTA_VISUAL_HEIGHT_PX }}
          >
            {isDefaultCTAVisual(visual) ? (
              <DefaultCTAVisualPreview src={visualSrc} />
            ) : (
              <CustomCTAVisualPreview src={visualSrc} />
            )}
          </div>
          {text.trim().length > 0 ? (
            <div className="bg-white px-6 py-4">{textBlock}</div>
          ) : null}
        </div>
        {closeButton}
        {showArrow && (
          <CTABubbleArrow alignment={alignment} buttonSize={buttonSize} />
        )}
      </div>
    );
  }

  return (
    <div
      className={cx(
        "relative w-full cursor-default select-none break-words rounded-[28px] bg-white px-6 py-4 shadow-[0_13px_56px_0_rgba(0,0,0,0.16)]"
      )}
    >
      {closeButton}
      {textBlock}
      {showArrow && (
        <CTABubbleArrow alignment={alignment} buttonSize={buttonSize} />
      )}
    </div>
  );
};

interface CTABubbleArrowProps {
  alignment: "left" | "right";
  buttonSize: number;
}

const CTABubbleArrow: FC<CTABubbleArrowProps> = ({ alignment, buttonSize }) => {
  return (
    <span
      className={cx(
        "pointer-events-none absolute bottom-[-8px] leading-[0]",
        alignment === "right" ? "translate-x-1/2" : "-translate-x-1/2"
      )}
      style={
        alignment === "right"
          ? { right: `${buttonSize / 2}px` }
          : { left: `${buttonSize / 2}px` }
      }
    >
      <ArrowIcon />
    </span>
  );
};
